import type { Rule } from "antd/lib/form";
import { phone } from "phone";
import { safeEvaluateExpression } from "./safeExpressionEvaluator";
export const rules: { [key: string]: Rule[] } = {
	fullName: [{ required: true, message: "Please input your full name" }],
	firstName: [{ required: true, message: "Please input your first name" }],
	lastName: [{ required: true, message: "Please input your last name" }],
	email: [
		{ required: true, message: "Please input your email" },
		{ type: "email", message: "Invalid email" },
	],
	// Aligned with the server-side Firebase Identity Platform password policy
	// applied at FirebaseService startup (equility-api). Both layers MUST stay
	// in sync — Firebase rejects non-compliant passwords on identitytoolkit
	// calls regardless of client validation. Per Intuit Standard Password
	// Policy + NIST 800-63B §5.1.1: min 12 chars with mixed character classes.
	password: [
		{ required: true, message: "Please input your password" },
		{
			pattern:
				/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#_\-+=~^.,:;'"()[\]{}|\\/<>])[A-Za-z\d@$!%*?&#_\-+=~^.,:;'"()[\]{}|\\/<>]{12,}$/,
			message:
				"Must contain at least 12 characters, one uppercase, one lowercase, one number and one special character",
		},
	],
	// Relaxed rule for login / current-password entry. Existing users may have
	// pre-policy passwords that no longer satisfy `rules.password`; Firebase's
	// server-side `forceUpgradeOnSignin` prompts them to rotate on next login,
	// so the client must not block submission.
	passwordLogin: [{ required: true, message: "Please input your password" }],
	phoneNumber: [
		{
			required: false,
			message: "Please input your phone number",
			validator: (_, value) => {
				if (!value) return Promise.resolve(); // No need to validate if not required and empty
				const { isValid } = phone(value); // Use phone to check if valid
				return isValid
					? Promise.resolve()
					: Promise.reject("Invalid phone number");
			},
		},
	],
	appsumo: [{ required: true, message: "Please input your AppSumo Code" }],
	terms: [
		{
			validator: (_, value) =>
				value
					? Promise.resolve()
					: Promise.reject(
							"Please indicate that you have read and agree to the Privacy Policy and Terms of Use",
						),
		},
	],
	source: [{ required: true, message: "Please select" }],
	sourcedetails: [{ required: true, message: "Please fill out this field" }],
};

export const customRules: { [key: string]: Rule[] } = {
	name: [{ required: true, message: "Please input your first name", max: 40 }],
	description: [
		{ required: true, message: "Please input your description", max: 100 },
	],
	account: [{ required: true, message: "Please select an account" }],
	formula: [
		{
			required: true,
			validator(rule, value) {
				const x = parseFormula(value[0]);
				if (!x) return Promise.reject("Invalid formula");
				try {
					// Use safe expression evaluator instead of eval
					safeEvaluateExpression(x);
					return Promise.resolve();
				} catch (error) {
					return Promise.reject(
						error instanceof Error ? error.message : "Invalid formula",
					);
				}
			},
			message: "Invalid formula",
			validateTrigger: ["onBlur", "onKeyDown"],
		},
	], // onChange by default
};

function parseFormula(value: any) {
	if (!value?.children) return;
	const arr = value.children.map((v: any) => {
		if (v.type === "badge") return "(1+1)";
		return v.text;
	});
	// eslint-disable-next-line
	var z = /^(\([+*%/\-\d\s]+\)|[+*%/\-\d\s]+)+$/.test(arr.join(""));
	return arr.join("");
}

export function generateOptionsFromEnum(data: object) {
	return Object.entries(data)
		.map(([label, value]) => ({ label, value }))
		.filter((opt) => typeof opt.value === "number");
}

export function getFileNameFromUrlWithQuery(url: string) {
	if (!url) {
		return "";
	}
	const urlWithoutQuery = url.split("?")[0];
	const parts = urlWithoutQuery.split("/");
	return parts[parts.length - 1];
}

export function formatFileFieldValue(files: { fileList: [] }) {
	return files?.fileList?.map((file: any) => file.response);
}
